import { useState, type FocusEvent } from 'react';
import { flushSync } from 'react-dom';
import { selectInputOnFocus } from '../utils/uiTheme';
import {
  clampDecimal,
  formatDecimalDisplay,
  parseDecimalInput,
} from '../utils/numberInput';

export interface DecimalInputProps {
  value: number;
  onCommit: (value: number) => void;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
  min?: number;
  max?: number;
  decimals?: number;
  formatDisplay?: (value: number) => string;
  /** When true, show empty string instead of formatted zero when not editing. */
  emptyWhenZero?: boolean;
}

export function DecimalInput({
  value,
  onCommit,
  disabled,
  className,
  placeholder,
  min,
  max,
  decimals = 2,
  formatDisplay,
  emptyWhenZero = false,
}: DecimalInputProps) {
  const [draft, setDraft] = useState<string | null>(null);

  const format = formatDisplay ?? ((n: number) => formatDecimalDisplay(n, decimals));

  const displayValue =
    draft !== null
      ? draft
      : emptyWhenZero && value === 0
        ? ''
        : format(value);

  const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
    setDraft(emptyWhenZero && value === 0 ? '' : format(value));
    selectInputOnFocus(e);
  };

  const handleBlur = () => {
    if (draft === null) return;
    const parsed = parseDecimalInput(draft);
    if (parsed !== null) {
      flushSync(() => {
        onCommit(clampDecimal(parsed, min, max));
      });
    }
    setDraft(null);
  };

  return (
    <input
      type="text"
      inputMode="decimal"
      value={displayValue}
      onChange={(e) => setDraft(e.target.value)}
      onFocus={handleFocus}
      onBlur={handleBlur}
      disabled={disabled}
      className={className}
      placeholder={placeholder}
    />
  );
}

export default DecimalInput;
